import React, { useState } from 'react';
import {
  User, Mail, Phone, MapPin, Home, Key,
  Shield, LogOut, Copy, CheckCircle, Clock,
  Hash, Edit3, Save, X
} from 'lucide-react';

const STORAGE_KEY = 'hoags_user_profile';

// ── load / save user profile from localStorage ─────────────────────────────
const loadProfile = (sub) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${sub}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const saveProfile = (sub, data) => {
  try { localStorage.setItem(`${STORAGE_KEY}_${sub}`, JSON.stringify(data)); } catch {}
};

// ── shared sub-components ──────────────────────────────────────────────────
const Section = ({ title, icon: Icon, color, children }) => (
  <div style={{
    background: '#fff', borderRadius: 18, padding: '20px 20px 14px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid rgba(0,0,0,0.06)', marginBottom: 16,
  }}>
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
      <div style={{
        width:30, height:30, borderRadius:8,
        background:`${color}18`,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <Icon size={15} color={color}/>
      </div>
      <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e', letterSpacing:-.2 }}>{title}</span>
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value, mono, copiable, copyKey, copied, onCopy, badge }) => (
  <div style={{
    display:'flex', justifyContent:'space-between', alignItems:'center',
    padding:'11px 16px', background:'rgba(0,0,0,0.02)', borderRadius:10, marginBottom:6,
  }}>
    <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500, minWidth:130 }}>{label}</span>
    <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, justifyContent:'flex-end' }}>
      {badge}
      <span style={{
        fontSize:13, color:'#1c1c1e', fontWeight:600,
        fontFamily: mono ? '"SF Mono",monospace' : 'inherit',
        wordBreak:'break-all', textAlign:'right', maxWidth:260,
      }}>{value}</span>
      {copiable && value !== '—' && (
        <button onClick={() => onCopy(value, copyKey)} style={{
          background:'none', border:'none', cursor:'pointer',
          color: copied === copyKey ? '#34C759' : '#C7C7CC',
          display:'flex', alignItems:'center', flexShrink:0, transition:'color .2s',
        }}>
          {copied === copyKey ? <CheckCircle size={13}/> : <Copy size={13}/>}
        </button>
      )}
    </div>
  </div>
);

const VerifiedBadge = ({ ok }) => (
  <span style={{
    fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20,
    background: ok ? 'rgba(52,199,89,.12)' : 'rgba(255,59,48,.1)',
    color: ok ? '#34C759' : '#FF3B30',
  }}>{ok ? 'Verified' : 'Unverified'}</span>
);

// ══════════════════════════════════════════════════════════════════════════
// USER Profile — editable name, address, mobile
// ══════════════════════════════════════════════════════════════════════════
function UserProfile({ session, onSignOut }) {
  const payload  = session?.getIdToken()?.payload || {};
  const sub      = payload.sub || '';
  const email    = payload.email || '—';
  const cogUser  = payload['cognito:username'] || '';
  const initial  = (email !== '—' ? email : cogUser).charAt(0).toUpperCase();

  // Load saved profile data
  const [profile, setProfile] = useState(() => loadProfile(sub));
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState({});
  const [saved,   setSaved]   = useState(false);

  const displayName = profile.name || email || cogUser || 'User';

  const startEdit = () => {
    setDraft({
      name:    profile.name    || '',
      mobile:  profile.mobile  || '',
      address: profile.address || '',
      city:    profile.city    || '',
      pincode: profile.pincode || '',
    });
    setEditing(true);
  };

  const handleSave = () => {
    const updated = { ...profile, ...draft };
    setProfile(updated);
    saveProfile(sub, updated);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleCancel = () => { setEditing(false); };

  const Field = ({ icon: Icon, label, field, placeholder, type = 'text' }) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:12, color:'#8E8E93', fontWeight:600, marginBottom:6, display:'block', letterSpacing:.2 }}>
        {label.toUpperCase()}
      </label>
      <div style={{ position:'relative' }}>
        <Icon size={14} style={{
          position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#C7C7CC',
        }}/>
        <input
          type={type}
          value={draft[field] || ''}
          onChange={e => setDraft(d => ({ ...d, [field]: e.target.value }))}
          placeholder={placeholder}
          style={{
            width:'100%', padding:'11px 14px 11px 36px',
            borderRadius:12, border:'1.5px solid #e5e5ea',
            fontSize:14, outline:'none', fontFamily:'inherit',
            color:'#1c1c1e', background:'#f9f9f9',
            boxSizing:'border-box',
            transition:'border-color .15s, box-shadow .15s',
          }}
          onFocus={e => { e.target.style.borderColor='#007AFF'; e.target.style.boxShadow='0 0 0 3px rgba(0,122,255,.12)'; }}
          onBlur={e  => { e.target.style.borderColor='#e5e5ea'; e.target.style.boxShadow='none'; }}
        />
      </div>
    </div>
  );

  return (
    <div style={{
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      maxWidth:540,
    }}>
      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:28, fontWeight:800, color:'#1c1c1e', letterSpacing:-.6, margin:0 }}>
          My Profile
        </h1>
        <p style={{ color:'#8E8E93', fontSize:14, marginTop:4 }}>
          Your personal information
        </p>
      </div>

      {/* Hero card */}
      <div style={{
        background:'linear-gradient(135deg,#0f172a,#1e3a5f)',
        borderRadius:20, padding:'24px 28px',
        display:'flex', alignItems:'center', gap:20,
        marginBottom:20, boxShadow:'0 8px 32px rgba(0,0,0,0.2)',
        flexWrap:'wrap',
      }}>
        <div style={{
          width:72, height:72, borderRadius:'50%',
          background:'linear-gradient(135deg,#007AFF,#0063CC)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:28, fontWeight:800, color:'#fff',
          boxShadow:'0 6px 20px rgba(0,122,255,.4)', flexShrink:0,
        }}>{initial}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:20, fontWeight:800, color:'#fff', letterSpacing:-.3, marginBottom:4 }}>
            {displayName}
          </div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,.55)' }}>{email}</div>
          {profile.city && (
            <div style={{ fontSize:12, color:'rgba(255,255,255,.4)', marginTop:4, display:'flex', alignItems:'center', gap:5 }}>
              <MapPin size={11}/> {profile.city}
            </div>
          )}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {!editing && (
            <button onClick={startEdit} style={{
              display:'flex', alignItems:'center', gap:6,
              background:'rgba(0,122,255,.2)', border:'1px solid rgba(0,122,255,.3)',
              borderRadius:10, padding:'8px 14px',
              cursor:'pointer', color:'#60aaff',
              fontSize:13, fontWeight:600, fontFamily:'inherit',
            }}>
              <Edit3 size={13}/> Edit
            </button>
          )}
          <button onClick={onSignOut} style={{
            display:'flex', alignItems:'center', gap:6,
            background:'rgba(255,59,48,.15)', border:'1px solid rgba(255,59,48,.3)',
            borderRadius:10, padding:'8px 14px',
            cursor:'pointer', color:'#FF6B6B',
            fontSize:13, fontWeight:600, fontFamily:'inherit',
          }}>
            <LogOut size={13}/> Sign Out
          </button>
        </div>
      </div>

      {/* Saved confirmation */}
      {saved && (
        <div style={{
          display:'flex', alignItems:'center', gap:10,
          background:'rgba(52,199,89,.08)', border:'1px solid rgba(52,199,89,.2)',
          borderRadius:12, padding:'12px 16px', marginBottom:16,
          animation:'fadeIn .3s ease',
        }}>
          <CheckCircle size={16} color="#34C759"/>
          <span style={{ fontSize:13, fontWeight:600, color:'#34C759' }}>Profile saved successfully!</span>
        </div>
      )}

      {/* Edit form */}
      {editing ? (
        <div style={{
          background:'#fff', borderRadius:18, padding:'22px 22px 18px',
          boxShadow:'0 2px 16px rgba(0,0,0,.08)',
          border:'1.5px solid rgba(0,122,255,.2)', marginBottom:16,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
            <div style={{
              width:30, height:30, borderRadius:8, background:'rgba(0,122,255,.1)',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <Edit3 size={15} color="#007AFF"/>
            </div>
            <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>Edit Personal Info</span>
          </div>

          <Field icon={User}   label="Full Name"  field="name"    placeholder="Your full name"/>
          <Field icon={Phone}  label="Mobile"     field="mobile"  placeholder="+91 98765 43210" type="tel"/>
          <Field icon={Home}   label="Address"    field="address" placeholder="House / flat / street"/>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <Field icon={MapPin} label="City"    field="city"    placeholder="City"/>
            <Field icon={MapPin} label="Pincode" field="pincode" placeholder="PIN code"/>
          </div>

          <div style={{ display:'flex', gap:10, marginTop:6 }}>
            <button onClick={handleCancel} style={{
              flex:1, padding:'12px', borderRadius:12,
              border:'1.5px solid #e5e5ea', background:'#fff',
              color:'#48484a', fontWeight:600, fontSize:14,
              cursor:'pointer', fontFamily:'inherit',
            }}>
              <X size={14} style={{marginRight:6, verticalAlign:'middle'}}/> Cancel
            </button>
            <button onClick={handleSave} style={{
              flex:2, padding:'12px', borderRadius:12, border:'none',
              background:'linear-gradient(135deg,#007AFF,#0063CC)',
              color:'#fff', fontWeight:700, fontSize:14,
              cursor:'pointer', fontFamily:'inherit',
              boxShadow:'0 4px 14px rgba(0,122,255,.3)',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            }}>
              <Save size={14}/> Save Profile
            </button>
          </div>
        </div>
      ) : (
        /* View mode */
        <Section title="Personal Information" icon={User} color="#007AFF">
          {[
            { label:'Full Name',  value: profile.name    || '—', icon: User    },
            { label:'Email',      value: email,                   icon: Mail    },
            { label:'Mobile',     value: profile.mobile  || '—', icon: Phone   },
            { label:'Address',    value: profile.address || '—', icon: Home    },
            { label:'City',       value: profile.city    || '—', icon: MapPin  },
            { label:'Pincode',    value: profile.pincode || '—', icon: MapPin  },
          ].map(({ label, value }) => (
            <div key={label} style={{
              display:'flex', justifyContent:'space-between', alignItems:'center',
              padding:'11px 16px', background:'rgba(0,0,0,0.02)',
              borderRadius:10, marginBottom:6,
            }}>
              <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500 }}>{label}</span>
              <span style={{
                fontSize:13, color: value === '—' ? '#C7C7CC' : '#1c1c1e',
                fontWeight:600, textAlign:'right', maxWidth:260,
              }}>{value}</span>
            </div>
          ))}
          <button onClick={startEdit} style={{
            width:'100%', marginTop:6, padding:'11px',
            borderRadius:12, border:'1.5px solid rgba(0,122,255,.2)',
            background:'rgba(0,122,255,.05)', color:'#007AFF',
            fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit',
            display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          }}>
            <Edit3 size={13}/> Edit Profile
          </button>
        </Section>
      )}

      {/* Account (read-only) */}
      <Section title="Account" icon={Mail} color="#AF52DE">
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'11px 16px', background:'rgba(0,0,0,0.02)', borderRadius:10, marginBottom:6,
        }}>
          <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500 }}>Email</span>
          <span style={{ fontSize:13, color:'#1c1c1e', fontWeight:600 }}>{email}</span>
        </div>
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'11px 16px', background:'rgba(0,0,0,0.02)', borderRadius:10,
        }}>
          <span style={{ fontSize:12, color:'#8E8E93', fontWeight:500 }}>User ID</span>
          <span style={{ fontSize:11, color:'#8E8E93', fontFamily:'"SF Mono",monospace' }}>
            {sub.slice(0,18)}…
          </span>
        </div>
      </Section>

      <style>{`@keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ADMIN Profile — full Cognito info, read-only
// ══════════════════════════════════════════════════════════════════════════
function AdminProfile({ session, onSignOut }) {
  const [copied, setCopied] = useState('');
  const [jwtOpen, setJwtOpen] = useState(false);
  const [jwtCopied, setJwtCopied] = useState(false);

  const payload     = session?.getIdToken()?.payload || {};
  const cognitoUser = payload['cognito:username'] || '';
  const username    = payload.email || cognitoUser || payload.sub?.slice(0,8) || '—';
  const email       = payload.email        || '—';
  const phone       = payload.phone_number || '—';
  const sub         = payload.sub          || '—';
  const groups      = payload['cognito:groups'] || [];
  const emailVerified = payload.email_verified;
  const phoneVerified = payload.phone_number_verified;
  const issuedAt    = payload.iat       ? new Date(payload.iat  * 1000) : null;
  const expiresAt   = payload.exp       ? new Date(payload.exp  * 1000) : null;
  const authTime    = payload.auth_time ? new Date(payload.auth_time * 1000) : null;
  const tokenUse    = payload.token_use          || '—';
  const issuer      = payload.iss?.replace('https://', '') || '—';
  const clientId    = payload.client_id || payload.aud || '—';
  const initial     = username.charAt(0).toUpperCase();

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(''), 2000);
    });
  };

  const fmt = (date) => date
    ? date.toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' })
    : '—';

  const Row = ({ label, value, mono, copiable, copyKey, badge }) => (
    <InfoRow label={label} value={value} mono={mono} copiable={copiable}
      copyKey={copyKey} copied={copied} onCopy={copyText} badge={badge}/>
  );

  return (
    <div style={{
      fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif",
      maxWidth:640,
    }}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontSize:28, fontWeight:800, color:'#1c1c1e', letterSpacing:-.6, margin:0 }}>Profile</h1>
        <p style={{ color:'#8E8E93', fontSize:14, marginTop:4 }}>Your Cognito account information</p>
      </div>

      {/* Hero */}
      <div style={{
        background:'linear-gradient(135deg,#1c1c1e,#3a3a3c)',
        borderRadius:20, padding:'24px 28px',
        display:'flex', alignItems:'center', gap:20,
        marginBottom:20, boxShadow:'0 8px 32px rgba(0,0,0,0.2)', flexWrap:'wrap',
      }}>
        <div style={{
          width:72, height:72, borderRadius:'50%',
          background:'linear-gradient(135deg,#7c3aed,#6d28d9)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:28, fontWeight:800, color:'#fff',
          boxShadow:'0 6px 20px rgba(124,58,237,.4)', flexShrink:0,
        }}>{initial}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:20, fontWeight:800, color:'#fff', letterSpacing:-.3, marginBottom:4 }}>
            {username}
          </div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,.55)', marginBottom:6 }}>
            {email !== '—' ? email : 'No email on record'}
          </div>
          <span style={{
            fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:20,
            background:'rgba(124,58,237,.3)', color:'#c4b5fd', letterSpacing:.4,
          }}>ADMINISTRATOR</span>
          {groups.length > 0 && (
            <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
              {groups.map(g => (
                <span key={g} style={{
                  fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:20,
                  background:'rgba(0,122,255,.25)', color:'#60aaff', letterSpacing:.4,
                }}>{g.toUpperCase()}</span>
              ))}
            </div>
          )}
        </div>
        <button onClick={onSignOut} style={{
          display:'flex', alignItems:'center', gap:6,
          background:'rgba(255,59,48,.15)', border:'1px solid rgba(255,59,48,.3)',
          borderRadius:10, padding:'8px 14px',
          cursor:'pointer', color:'#FF6B6B',
          fontSize:13, fontWeight:600, fontFamily:'inherit', flexShrink:0,
        }}>
          <LogOut size={13}/> Sign Out
        </button>
      </div>

      {/* JWT removed — Transaction History below */}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Transaction History (shared for both roles)
// ══════════════════════════════════════════════════════════════════════════
function TxnHistory() {
  const [history, setHistory] = React.useState([]);
  const [open,    setOpen]    = React.useState(true);

  React.useEffect(() => {
    try {
      const h = JSON.parse(localStorage.getItem('hoags_txn_history') || '[]');
      setHistory(h);
    } catch(e) {}
  }, []);

  const CMD_LABELS = {
    RC:'Recharge', AL:'Add Litres', EV:'Extend Validity',
    RL:'Reverse Litres', RV:'Reverse Validity',
  };
  const CMD_COLORS = {
    RC:'#34C759', AL:'#007AFF', EV:'#AF52DE',
    RL:'#FF9F0A', RV:'#FF3B30',
  };
  const CMD_EMOJI = {
    RC:'💳', AL:'💧', EV:'📅', RL:'↩️', RV:'⏪',
  };

  const fmt = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day:'2-digit', month:'short',
      year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true });
  };

  if (history.length === 0) return null;

  return (
    <div style={{
      background:'#fff', borderRadius:20,
      boxShadow:'0 2px 16px rgba(0,0,0,.07)',
      border:'1px solid rgba(0,0,0,.06)', marginBottom:16, overflow:'hidden',
    }}>
      <div onClick={()=>setOpen(o=>!o)} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'16px 20px', cursor:'pointer', userSelect:'none',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:30, height:30, borderRadius:8,
            background:'rgba(52,199,89,.1)',
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>
            📊
          </div>
          <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>Transaction History</span>
          <span style={{ fontSize:10, color:'#8E8E93', background:'rgba(0,0,0,.05)',
            padding:'2px 8px', borderRadius:20, fontWeight:500 }}>{history.length}</span>
        </div>
        <span style={{ fontSize:11, color:'#C7C7CC' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ borderTop:'1px solid rgba(0,0,0,.06)', maxHeight:420, overflowY:'auto' }}>
          {history.map((tx, i) => {
            const color = CMD_COLORS[tx.type] || '#8E8E93';
            const emoji = CMD_EMOJI[tx.type]  || '📦';
            const label = CMD_LABELS[tx.type] || tx.type;
            const r = tx.response || {};
            return (
              <div key={i} style={{
                padding:'12px 20px',
                borderBottom: i<history.length-1 ? '1px solid rgba(0,0,0,.05)' : 'none',
                display:'flex', gap:12, alignItems:'flex-start',
              }}>
                {/* Icon */}
                <div style={{
                  width:38, height:38, borderRadius:11, flexShrink:0,
                  background:`${color}15`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:18,
                }}>{emoji}</div>

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>{label}</span>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:20,
                      background:`${color}15`, color,
                    }}>{tx.type}</span>
                    {r.S === 'T' && (
                      <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:20,
                        background:'rgba(52,199,89,.1)', color:'#34C759' }}>✓ Success</span>
                    )}
                  </div>
                  <div style={{ fontSize:11, color:'#48484a', marginBottom:2 }}>
                    {tx.device} · {tx.mac}
                  </div>
                  <div style={{ fontSize:11, color:'#8E8E93', lineHeight:1.6 }}>
                    {r.NL  && <span>💧 {r.NL} L &nbsp;</span>}
                    {r.NV  && <span>📅 {r.NV} &nbsp;</span>}
                    {r.V   && !r.NV && <span>📅 {r.V} &nbsp;</span>}
                    {tx.price && <span>💰 {tx.price} &nbsp;</span>}
                    {r.X   && <span style={{fontSize:10,color:'#C7C7CC'}}>Txn: {r.X}</span>}
                  </div>
                  <div style={{ fontSize:10, color:'#C7C7CC', marginTop:3 }}>{fmt(tx.time)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {history.length > 0 && (
        <div style={{ padding:'10px 20px', borderTop:'1px solid rgba(0,0,0,.05)' }}>
          <button onClick={()=>{ localStorage.removeItem('hoags_txn_history'); setHistory([]); }}
            style={{ fontSize:11, color:'#FF3B30', background:'none', border:'none',
              cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>
            Clear History
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Root export — picks correct profile based on role
// ══════════════════════════════════════════════════════════════════════════
export default function Profile({ session, onSignOut, role }) {
  if (role === 'admin') {
    return <><AdminProfile session={session} onSignOut={onSignOut}/><TxnHistory/></>;
  }
  return <><UserProfile session={session} onSignOut={onSignOut}/><TxnHistory/></>;
}
